import Box from "@mui/material/Box";
import Slide from "@mui/material/Slide";
import Typography from "@mui/material/Typography";
import { useEffect, useState } from "react";

import APButton from "../../../components/buttons/APButton";
import APCard, { APCardColor } from "../../../components/cards/APCard";
import RestrictedAppScope from "../../../components/RestrictedAppScope";
import { toDollars } from "../../../helpers/currencies";
import { formatDate } from "../../../helpers/dates";
import { isExpenseClaimCancelable } from "../../../helpers/expenseClaims";
import { useApi } from "../../../providers/ApiProvider";
import {
  ExpenseClaim,
  ExpenseClaimLabelMap,
  ExpenseClaimStatus,
} from "../../../types/ExpenseClaim";
import { CRMAppLocationIdentifier, FaqProps } from "../../../types/Faq";

const expenseClaimColors: Record<ExpenseClaimStatus, APCardColor> = {
  "awaiting-approval": "primary",
  approved: "primary",
  "ready-for-payment": "tertiaryGreen",
  "payment-submitted": "tertiaryGreen",
  paid: "neutralWhite",
  declined: "neutralGray",
  cancelled: "neutralGray",
};

export const ExpenseClaimStatusLabelMap: Record<ExpenseClaimStatus, string> = {
  "awaiting-approval": "Awaiting Approval",
  approved: "Approved",
  "ready-for-payment": "Ready for Payment",
  "payment-submitted": "Payment Submitted",
  paid: "Paid",
  declined: "Declined",
  cancelled: "Cancelled",
};

const ExpenseClaimCard = ({
  expenseClaim,
  primaryAmount,
  faqs,
  showNegativeAmount = false,
  etaFaqAppLocationIdentifier,
  onCancelExpenseClaim,
}: {
  expenseClaim: ExpenseClaim;
  primaryAmount: "exGst" | "incGst";
  faqs?: FaqProps;
  showNegativeAmount?: boolean;
  etaFaqAppLocationIdentifier: CRMAppLocationIdentifier;
  onCancelExpenseClaim?: (claimId: string) => void;
}) => {
  const [animationDelayed, setAnimationDelayed] = useState(true);
  const { fetchFile } = useApi();

  useEffect(() => {
    const timer = setTimeout(() => setAnimationDelayed(false), 300);
    return () => clearTimeout(timer);
  }, []);

  const handleDownload = async (
    claimId: string,
    fileName: string,
    type: "taxInvoice" | "attachment"
  ) => {
    fetchFile("getExpenseClaimFile", expenseClaim.dossierId, fileName, {
      queryParams: {
        claimId: claimId,
        type: type,
      },
    });
  };

  const renderDownloadLink = (
    claimId: string,
    fileName: string,
    type: "taxInvoice" | "attachment"
  ) => {
    return (
      <Typography
        variant="bodySmall"
        color="primary"
        onClick={() => handleDownload(claimId, fileName, type)}
        sx={{ textDecoration: "underline", cursor: "pointer" }}
      >
        {fileName}
      </Typography>
    );
  };

  const renderTitleLeft = () => {
    return (
      <Typography variant="titleMedium">
        {formatDate(expenseClaim.transactionDate)} -{" "}
        {ExpenseClaimLabelMap[expenseClaim.type]}
      </Typography>
    );
  };

  const renderBodyContent = () => {
    let statusText;
    let estimatedPaymentDateText;
    let faqAppLocationIdentifier: CRMAppLocationIdentifier | undefined;

    switch (expenseClaim.status) {
      case "awaiting-approval":
      case "approved":
      case "ready-for-payment":
      case "payment-submitted": {
        estimatedPaymentDateText = expenseClaim.paymentEtaText
          ? `: ETA ${expenseClaim.paymentEtaText} *`
          : "";
        statusText = `${ExpenseClaimStatusLabelMap[expenseClaim.status]}`;
        faqAppLocationIdentifier = expenseClaim.paymentEtaText
          ? etaFaqAppLocationIdentifier
          : undefined;
        break;
      }
      case "paid": {
        const paymentText = expenseClaim.paidDate
          ? `: ${formatDate(expenseClaim.paidDate)}`
          : "";
        statusText = `${
          ExpenseClaimStatusLabelMap[expenseClaim.status]
        }${paymentText}`;
        break;
      }
      case "declined":
        {
          const reasonText = expenseClaim.noteToDriver
            ? `: ${expenseClaim.noteToDriver}`
            : "";

          statusText = `${
            ExpenseClaimStatusLabelMap[expenseClaim.status]
          }${reasonText}`;
        }
        break;
      case "cancelled": {
        statusText = `${ExpenseClaimStatusLabelMap[expenseClaim.status]}`;
        break;
      }
    }

    const statusElement = (
      <Box sx={{ display: "flex" }}>
        <Typography variant="bodyMedium">{statusText}</Typography>
        <Box sx={{ overflow: "hidden" }}>
          <Slide
            in={!!estimatedPaymentDateText}
            direction="right"
            appear={!animationDelayed}
          >
            <Box>
              <Typography variant="bodyMedium">
                {" "}
                {estimatedPaymentDateText}
              </Typography>
            </Box>
          </Slide>
        </Box>
      </Box>
    );
    if (faqAppLocationIdentifier && faqs) {
      return faqs.renderAppLocation(
        faqAppLocationIdentifier,
        statusElement,
        false
      );
    }

    return statusElement;
  };

  const renderExpandContent = () => {
    const amount =
      primaryAmount === "exGst" ? expenseClaim.incGst : expenseClaim.exGst;
    const amountLabel = primaryAmount === "exGst" ? "Inc GST" : "Ex GST";

    return (
      <Box display="flex" flexDirection="column" gap={1}>
        {expenseClaim.vendor && (
          <Typography variant="bodyMedium">{expenseClaim.vendor}</Typography>
        )}
        <Typography variant="bodyMedium">{expenseClaim.description}</Typography>
        <Typography variant="bodyMedium">
          {amountLabel}: {toDollars(showNegativeAmount ? -amount : amount, 2)}
        </Typography>
        {expenseClaim.taxInvoiceId &&
          expenseClaim.taxInvoiceName &&
          renderDownloadLink(
            expenseClaim.id,
            expenseClaim.taxInvoiceName || "",
            "taxInvoice"
          )}
        {expenseClaim.attachmentId &&
          expenseClaim.attachmentName &&
          renderDownloadLink(
            expenseClaim.id,
            expenseClaim.attachmentName || "",
            "attachment"
          )}
        {expenseClaim.bank && (
          <>
            <Typography variant="bodyMedium">
              BSB: {expenseClaim.bank.bsb}
            </Typography>
            <Typography variant="bodyMedium">
              Account: {expenseClaim.bank.accountNumber}
            </Typography>
          </>
        )}
        <RestrictedAppScope scope="external">
          {isExpenseClaimCancelable(expenseClaim.status) &&
            onCancelExpenseClaim && (
              <Box sx={{ display: "flex", justifyContent: "flex-end" }} mt={1}>
                <APButton
                  text="Cancel"
                  color="neutralWhite"
                  textColor="primary"
                  variant="contained"
                  onClick={() => onCancelExpenseClaim?.(expenseClaim.id)}
                  icon="delete"
                />
              </Box>
            )}
        </RestrictedAppScope>
      </Box>
    );
  };

  const renderTitleRight = () => {
    const amount =
      primaryAmount === "exGst" ? expenseClaim.exGst : expenseClaim.incGst;

    return (
      <Typography variant="titleMedium">
        {toDollars(showNegativeAmount ? -amount : amount, 2)}
      </Typography>
    );
  };

  return (
    <APCard
      titleLeft={renderTitleLeft()}
      bodyContent={renderBodyContent()}
      expandContent={renderExpandContent()}
      titleRight={renderTitleRight()}
      color={expenseClaimColors[expenseClaim.status]}
      expandOnTitleClick={true}
    />
  );
};

export default ExpenseClaimCard;
